import { lazy, Suspense, useMemo, useState, useEffect } from "react";
import {
  PROXY_ACTIVATED_EVENT,
  hasProxyActivatedFired,
} from "../../../modules/domainGuard";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  useActivityPopupPriority,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import type { RedPacketPopupResult } from "./useRedPacketPopup.types";
import { ROUTES } from "../../../utils/navigation";

const RedPacketRain = lazy(() =>
  import("../components/RedPacketRain/RedPacketRain").then(m => ({
    default: m.RedPacketRain,
  }))
);

const RedPacketRainContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <RedPacketRain visible={visible} onClose={handleClose} />
    </Suspense>
  );
};

const RED_PACKET_CONFIG = POPUP_CONFIGS.RED_PACKET;

export const useRedPacketPopup = (): RedPacketPopupResult => {
  const {
    data: userInfo,
    isAuthenticated,
    loadRedpacketStatus,
  } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 红包已移出 user_info，由本组件(全局 GlobalEvent)挂载时按需拉取(NoAuth，游客也可)；
  // 登录态变化后重拉以更新用户维度的可领状态
  useEffect(() => {
    loadRedpacketStatus().catch(() => {});
  }, [isAuthenticated, loadRedpacketStatus]);

  // 域名容灾软恢复：红包状态属于首波请求，主域名被封时会失败，而上面那个 effect
  // 的依赖（isAuthenticated）不变就不会重试，不补的话红包状态整场缺失。
  //
  // 本 hook 经 useFloatingIcons 挂在 FloatingIconsProvider 上，而该 provider 已改为
  // lazy（见 App.tsx）——【订阅可能晚于派发】：chunk 落地前事件已经派发过就永久错过。
  // 所以 mount 时先查一次 hasProxyActivatedFired() 补课，再订阅后续翻转。
  useEffect(() => {
    const onProxyActivated = () => {
      loadRedpacketStatus().catch(() => {});
    };
    if (hasProxyActivatedFired()) onProxyActivated();
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, [loadRedpacketStatus]);

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.RED_PACKET_RAIN,
    RED_PACKET_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  const redStartMs = userInfo?.redpacket?.siteConf?.startTime;
  const redEndMs = userInfo?.redpacket?.siteConf?.endTime;

  const redDecide = useMemo(() => {
    const now = Date.now();
    if (redStartMs && redEndMs) {
      const isCountingToEnd = now >= redStartMs;
      return {
        targetMs: isCountingToEnd ? redEndMs : redStartMs,
        isOpen:
          isCountingToEnd &&
          (userInfo?.redPacketReceivable || !isAuthenticated),
      };
    }
    if (redStartMs) return { targetMs: redStartMs, isOpen: false };
    if (redEndMs) return { targetMs: redEndMs, isOpen: true };
    return { targetMs: now, isOpen: false };
  }, [redStartMs, redEndMs, userInfo?.redPacketReceivable, isAuthenticated]);

  const shouldShow = !!userInfo?.redpacket;

  const { trigger } = useQueuedPopup({
    popupId: RED_PACKET_CONFIG.id,
    priority,
    shouldShow: () => isConfigReady && !!userInfo?.redpacket,
    renderContent: onComplete => (
      <RedPacketRainContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: false,
    allowedPaths: [
      ROUTES.HOME,
      ROUTES.HOME_ALT,
      "/",
      ROUTES.EVENT_CENTER,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
    ],
    dependencies: [isConfigReady, userInfo?.redpacket],
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const remainingMs = Math.max(0, redDecide.targetMs - Date.now());

  return {
    shouldShow,
    trigger,
    remainingMs,
    isOpen: redDecide.isOpen,
  };
};
